import { Box } from '@mui/material';
import { useEffect, useState, useCallback, useMemo } from 'react';

interface FloatingBubbleProps {
  initialX: number;
  initialY: number;
  size: number;
  duration: number;
  delay: number;
  
}

export const FloatingBubble = ({ 
  initialX, 
  initialY, 
  size, 
  duration, 
  delay 
}: FloatingBubbleProps) => {
  const [isVisible, setIsVisible] = useState(true);

  // Memoized animation values for better performance
  const animationValues = useMemo(() => {
    const midY = initialY / 2;
    const endY = initialY + 100;
    const sinX20 = Math.sin(initialX) * 20;
    const sinX40 = Math.sin(initialX) * 40;
    
    return {
      midY,
      endY,
      sinX20,
      sinX40
    };
  }, [initialX, initialY]);

  // Optimized cleanup with useCallback
  const handleTimeout = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    const totalDuration = duration * 1000 + delay * 1000;
    const timer = setTimeout(handleTimeout, totalDuration);

    return () => clearTimeout(timer);
  }, [duration, delay, handleTimeout]);

  // Prefer CSS over JavaScript animations when possible
  const bubbleStyles = useMemo(() => ({
    position: 'absolute' as const,
    left: initialX,
    bottom: -size,
    width: size,
    height: size,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.7,
    zIndex: 1,
    animation: `floatUp ${duration}s ease-in-out ${delay}s both`,
    pointerEvents: 'none' as const,
    willChange: 'transform, opacity', // Hint browser for better performance
    '@keyframes floatUp': {
      '0%': {
        transform: 'translateY(0) translateX(0) scale(1)',
        opacity: 0.7
      },
      '50%': {
        transform: `translateY(-${animationValues.midY}px) translateX(${animationValues.sinX20}px) scale(1.05)`,
        opacity: 0.85
      },
      '100%': {
        transform: `translateY(-${animationValues.endY}px) translateX(${animationValues.sinX40}px) scale(1.1)`,
        opacity: 0
      }
    }
  }), [initialX, size, duration, delay, animationValues]);

  if (!isVisible) return null;

  return (
    <Box
      component="img"
      src="/bubble.png" // Fixed path - removed 'my-app/public'
      alt="Floating bubble"
      loading="lazy" // Better loading performance
      sx={bubbleStyles}
    />
  );
};